import React from 'react';
import { CheckCircle, Paperclip, BarChart3, MessageSquare, HelpCircle } from 'lucide-react';

interface Question {
  id: string;
  question: string;
  answer?: string;
  isCollapsed?: boolean;
  field?: string;
  type?: string;
  questionNumber?: number;
  isCounterQuestion?: boolean;
}

interface InterviewResponse {
  session_id: string;
  interview_phase: string;
  project_details: {
    project_number: number | null;
    project_name: string | null;
    project_main_question_number: number | null;
    total_questions_planned_for_project: number | null;
  };
  is_interview_complete: boolean;
  main_question_number: number;
  main_question: string;
  counter_question_number: number | null;
  counter_question: string | null;
  total_questions_planned: number;
  reasoning_references: Array<{
    references: {
      file_name: string;
      page_number: number;
    };
    reasoning: string;
    gap_areas_focused: string;
  }>;
}

interface QuestionsSidebarProps {
  questionsAndResponses: Question[];
  currentQuestion: Question | undefined;
  uploadedDocuments: { [questionId: string]: File[] };
  interviewComplete: boolean;
  currentInterviewData?: InterviewResponse | null;
  onQuestionClick: (questionId: string) => void;
  onToggleQuestionCollapse: (id: string) => void;
}

const QuestionsSidebar: React.FC<QuestionsSidebarProps> = ({
  questionsAndResponses,
  currentQuestion,
  uploadedDocuments,
  interviewComplete,
  currentInterviewData,
  onQuestionClick,
  onToggleQuestionCollapse
}) => {
  // Calculate progress stats
  const answeredQuestions = questionsAndResponses.filter(q => q.answer && q.answer.trim() !== '').length;
  const totalQuestions = currentInterviewData?.total_questions_planned || 1;
  
  console.log("Total Questions:", questionsAndResponses);
  // Get current question number from the API data
  const currentQuestionNumber =  currentInterviewData?.main_question_number || 0;
  const progressPercentage = totalQuestions > 0 ? Math.round((currentQuestionNumber / totalQuestions) * 100) : 0;

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sticky top-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        Questions & Responses
      </h2>
      <p className="text-gray-600 mb-6 leading-relaxed">
        Track your progress and review previous answers
      </p>

      {/* Progress Stats */}
      {currentInterviewData && (
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
          <div className="flex items-center gap-3 mb-3">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            <span className="font-semibold text-purple-900">Progress Overview</span>
          </div>
          
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center">
              <div className="text-xl font-bold text-purple-600">
                {currentQuestionNumber}
              </div>
              <div className="text-xs text-purple-700">Current</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-purple-600">
                {answeredQuestions}
              </div>
              <div className="text-xs text-purple-700">Answered</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-purple-600">
                {totalQuestions}
              </div>
              <div className="text-xs text-purple-700">Total</div>
            </div>
          </div>
          
          <div className="w-full bg-purple-200 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${progressPercentage}%`, 
                background: "linear-gradient(90deg, #bb1ccc 0%, #d946ef 100%)" 
              }}
            ></div>
          </div>
          
          <div className="text-center mt-2">
            <span className="text-sm font-medium text-purple-700">
              {progressPercentage}% Complete
            </span>
          </div>
        </div>
      )}

      <div style={{maxHeight:"calc(100vh - 450px)"}} className="space-y-4 overflow-y-auto overflow-hidden">
        {questionsAndResponses.map((q, index) => {
          const isCurrentQuestion = q.id === currentQuestion?.id;
          const hasDocuments = uploadedDocuments[q.id]?.length > 0;

          return (
            <div
              key={q.id}
              style={{
                borderColor: isCurrentQuestion ? "#bb1ccc" : "#e5e7eb",
              }}
              className={`border-2 rounded-xl overflow-hidden transition-all duration-200 ${
                isCurrentQuestion ? "bg-purple-50 shadow-md" : "hover:shadow-sm"
              }`}
            >
              <button
                onClick={() => {
                  onQuestionClick(q.id);
                  onToggleQuestionCollapse(q.id);
                }}
                className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {q.isCounterQuestion ? (
                        <HelpCircle className="w-4 h-4 text-orange-600" />
                      ) : (
                        <MessageSquare className="w-4 h-4 text-blue-600" />
                      )}
                      <span
                        style={{
                          background: q.answer
                            ? "#22c55e"
                            : isCurrentQuestion
                            ? "#bb1ccc"
                            : "#6b7280",
                          color: "#ffffff",
                        }}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md"
                      >
                        {q.questionNumber || index + 1}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">
                        {q.isCounterQuestion ? 'Follow-up' : 'Question'} {q.questionNumber || index + 1}
                      </span>
                      {q.type && (
                        <span className="block text-xs text-gray-500 mt-1">
                          {q.isCounterQuestion ? 'Counter Question' : 'Main Question'}
                        </span>
                      )}
                      {isCurrentQuestion && (
                        <span
                          style={{ color: "#bb1ccc" }}
                          className="block text-xs font-medium mt-1"
                        >
                          (Current)
                        </span>
                      )}

                      {hasDocuments && (
                        <div className="flex items-center gap-1 mt-2">
                          <Paperclip className="w-3 h-3 text-blue-600" />
                          <span className="text-xs text-blue-600">
                            {uploadedDocuments[q.id].length} doc(s)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {q.answer && (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                    {hasDocuments && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    )}
                  </div>
                </div>
              </button>

              {!q.isCollapsed && (
                <div className="px-4 pb-4 border-t border-gray-200">
                  <p className="text-gray-600 mb-3 py-3 leading-relaxed">
                    {q.question}
                  </p>
                  {q.answer && (
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-gray-800 leading-relaxed">{q.answer}</p>
                    </div>
                  )}
                  {hasDocuments && (
                    <div className="bg-blue-50 rounded-lg p-3 mt-1">
                      <h5 className="text-sm font-medium text-blue-900 mb-2">
                        Attached Documents:
                      </h5>
                      <div className="space-y-1">
                        {uploadedDocuments[q.id].map((file, fileIndex) => (
                          <div key={fileIndex} className="flex items-center gap-2 text-xs text-blue-800">
                            <Paperclip className="w-3 h-3" />
                            <span className="truncate">{file.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Interview completion indicator */}
      {interviewComplete && (
        <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-200 rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <span className="font-semibold text-green-800 text-lg">
              Interview Complete
            </span>
          </div>
          <p className="text-green-700 leading-relaxed">
            All questions have been answered. Ready to generate summary.
          </p>
        </div>
      )}
    </div>
  );
};

export default QuestionsSidebar;